import { ApiError } from './errors';

// In production, use the absolute backend URL.
// In development, Vite proxy handles /adm -> backend.
export const API_BASE = import.meta.env.PROD
  ? 'https://backend.rainmakerapp.io/adm'
  : '/adm';

/**
 * Extract a human-readable message from a parsed error response body.
 * Handles FastAPI's `detail` array shape (validation errors) as well as
 * simpler `{ detail: string }`, `{ message: string }`, and `{ error: string }` shapes.
 */
export function extractErrorMessage(parsed: unknown, status: number): string {
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;

    // FastAPI validation: detail is an array of { type, loc, msg, input }
    if (Array.isArray(obj.detail)) {
      const joined = obj.detail
        .map((e) => {
          if (e && typeof e === 'object') {
            const item = e as Record<string, unknown>;
            const loc = Array.isArray(item.loc) ? item.loc.join('.') : '';
            const msg = typeof item.msg === 'string' ? item.msg : '';
            return loc && msg ? `${loc}: ${msg}` : msg || loc;
          }
          return typeof e === 'string' ? e : '';
        })
        .filter(Boolean)
        .join('; ');
      if (joined) return joined;
    }

    if (typeof obj.detail === 'string') return obj.detail;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.error === 'string') return obj.error;
  }
  return `Request failed with status ${status}`;
}

/**
 * API client targeting /adm.
 * Throws ApiError on non-2xx responses.
 * 401 responses clear session storage and redirect to /login.
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      sessionStorage.clear();
      if (
        !window.location.pathname.startsWith('/login') &&
        window.location.pathname !== '/'
      ) {
        window.location.href = '/login';
      }
      throw new ApiError(401, 'Session expired');
    }

    const parsed = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      extractErrorMessage(parsed, response.status),
      parsed
    );
  }

  return response.json();
}

/**
 * Auth-specific client targeting /adm.
 * Does not throw on non-2xx — returns structured error instead.
 */
export async function authClient<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data?: T; error?: { status: number; message: string } }> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          message: extractErrorMessage(data, response.status),
        },
      };
    }

    return { data: data as T };
  } catch (err) {
    return {
      error: {
        status: 0,
        message: err instanceof Error ? err.message : 'Network error',
      },
    };
  }
}
